import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Keycloak, { KeycloakConfig, KeycloakProfile } from 'keycloak-js';
import { map, Observable, of, ReplaySubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OpenIdProfileInterface } from '../interface/open-id-profile.interface';

export class OpenIdProfile implements OpenIdProfileInterface {
    public isOperator: boolean = false;
    public isMember: boolean = false;
    public canCreateSubscription?: boolean = false;

    constructor(
        public id: string,
        public email: string,
        public firstName: string,
        public lastName: string,
        public username: string,
        public createdTimestamp: number
    ) {}
}
// https://barter-dev.albodigital.ca:7024/login/token
@Injectable({
    providedIn: 'root',
})
export class OpenIdService {
    public $bearerToken = new ReplaySubject<string>();
    public $userProfile = new ReplaySubject<OpenIdProfileInterface>();

    public keycloak: Keycloak;
    public initialized = false;

    private refreshToken = '';
    private keycloakAdminUrl: string;

    constructor(private httpClient: HttpClient) {
        let config: KeycloakConfig = {
            url: environment.keycloak.host,
            realm: environment.keycloak.realm,
            clientId: environment.keycloak.clientId,
        };

        this.keycloakAdminUrl = `${config.url}/admin/realms/${config.realm}`;
        this.keycloak = new Keycloak(config);
    }

    public init() {
        this.initialized = true;
        this.refreshToken = '';
        this.keycloak
            .init({
                onLoad: 'check-sso',
                checkLoginIframe: false,
                silentCheckSsoRedirectUri:
                    window.location.origin + '/assets/silent-check-sso.html',
            })
            .then((authenticated) => {
                if (!authenticated) {
                    // announce we tried and failed so open id guard can process
                    this.$bearerToken.next('');
                    return;
                }
                this.$bearerToken.next(String(this.keycloak!.token));
                this.maintainTokenValidity();
                this.keycloak
                    .loadUserProfile()
                    .then((keycloakProfile: KeycloakProfile) => {
                        if (!keycloakProfile) return;

                        let openIdProfile = new OpenIdProfile(
                            keycloakProfile.id!,
                            keycloakProfile.email!,
                            keycloakProfile.firstName!,
                            keycloakProfile.lastName!,
                            keycloakProfile.username!,
                            keycloakProfile.createdTimestamp!
                        );

                        if (
                            this.keycloak.hasRealmRole('barter-ledger-member')
                        ) {
                            openIdProfile.isMember = true;
                        }

                        if (
                            this.keycloak.hasRealmRole('barter-ledger-operator')
                        ) {
                            openIdProfile.isOperator = true;
                        }

                        if (
                            this.keycloak.hasRealmRole(
                                'barter-ledger-subscriptions'
                            )
                        ) {
                            openIdProfile.canCreateSubscription = true;
                        }

                        this.$userProfile.next(openIdProfile);
                    });
            });
    }

    public getUsersWithRole(role: string, offset: number = 0) {
        let url = `${this.keycloak.authServerUrl}/admin/realms`;
        url = `${url}/${this.keycloak.realm}/roles/${role}/users`;
        let params = new HttpParams().set('first', offset).set('max', 2);

        return this.httpClient.get<KeycloakProfile[]>(url, { params: params });
    }

    private maintainTokenValidity() {
        // start a timer 
        this.keycloak.updateToken(30).then((refreshed) => {
            //if (refreshed) console.log('RefreshedToken');
            setTimeout(() => {
                this.maintainTokenValidity();
            }, 30 * 1000);
        })
    }

    public openIdProfileFactory(keycloakProfile: KeycloakProfile) {
        let url = `${this.keycloak.authServerUrl}/admin/realms`;
        url = `${url}/${this.keycloak.realm}/users/${keycloakProfile.id}`;
        url = `${url}/role-mappings/realm`;

        return this.httpClient.get<any[]>(url).pipe(
            map((roles: any[]) => {
                let openIdProfile = new OpenIdProfile(
                    keycloakProfile.id!,
                    keycloakProfile.email!,
                    keycloakProfile.firstName!,
                    keycloakProfile.lastName!,
                    keycloakProfile.username!,
                    keycloakProfile.createdTimestamp!
                );

                for (let role of roles) {
                    if (role.name == 'barter-ledger-member') {
                        openIdProfile.isMember = true;
                    }

                    if (role.name == 'barter-ledger-operator') {
                        openIdProfile.isOperator = true;
                    }

                    if (role.name == 'barter-ledger-subscriptions') {
                        openIdProfile.canCreateSubscription = true;
                    }
                }

                return openIdProfile;
            })
        );
    }

    public searchUsers(searchValue?: string): Observable<KeycloakProfile[]> {
        let url = `${this.keycloak.authServerUrl}/admin/realms`;
        url = `${url}/${this.keycloak.realm}/users/`;

        if (!searchValue) return this.httpClient.get<KeycloakProfile[]>(url);

        let options = {
            params: new HttpParams().set('search', searchValue),
        };

        return this.httpClient.get<KeycloakProfile[]>(url, options);
    }
}
